import { shortenNumber } from '@/utils/number.utils';
import { Tooltip } from 'flowbite';

export type ChartContext = {
  el: HTMLElement;
};
export type ChartConfig = {
  selectedDataPoints: unknown[][];
  dataPointIndex: number;
  seriesIndex: number;
  w: {
    globals: {
      labels: string[];
      series: number[];
    };
  };
};

export const buildDonutChartTooltip = (conf: ChartConfig): string => {
  // There is no type for ApexCharts tooltip configuration
  // => Use any to avoid type checking
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globals = conf.w.globals;
  const label = globals.labels[conf.seriesIndex];
  const series = globals.series;
  const seriesTotal = series.reduce((acc: number, curr: number) => acc + curr, 0);
  const value = parseFloat(`${series[conf.seriesIndex]}`);
  const percent = (value / seriesTotal) * 100;

  return `<div class="donut-chart-tooltip">
    <div class="first-line"><div class="label">${label}</div></div>
    <div class="second-line">
      <div class="percent">${percent.toFixed(1)}%</div>
      <div class="value">${shortenNumber(value)}</div>
    </div>
  </div>`;
};

export const buildTooltipEl = (tooltipText: string, isInvisible = true): HTMLElement => {
  const tooltipEl = document.createElement('div');
  tooltipEl.innerHTML = tooltipText;
  tooltipEl.setAttribute('role', 'tooltip');
  tooltipEl.setAttribute('class', `common-tooltip ${isInvisible ? 'invisible opacity-0' : ''}`);
  return tooltipEl;
};

export const buildTooltipElementFromTitleEl = (titleEl: HTMLElement, removeTitleEl = true): HTMLElement => {
  const tooltipEl = buildTooltipEl(titleEl.innerHTML);
  document.body.appendChild(tooltipEl);
  if (removeTitleEl) {
    titleEl?.remove();
  }
  return tooltipEl;
};

export const overrideAxisTooltips = (axisEl: HTMLElement): [HTMLElement[], () => void] => {
  const textEls = axisEl.querySelectorAll('text');
  const tooltipEls: HTMLElement[] = [];
  const unsubscribers: (() => void)[] = [];

  textEls?.forEach((textEl, index) => {
    const titleEl = textEl.querySelector('title');
    if (titleEl) {
      const tooltipEl = buildTooltipElementFromTitleEl(titleEl);
      tooltipEls[index] = tooltipEl;
      const tooltipInstance = new Tooltip(tooltipEl, textEl as unknown as HTMLElement);
      unsubscribers.push(() => {
        tooltipEl?.remove();
        tooltipInstance.destroy();
      });
    } else {
      tooltipEls[index] = null;
    }
  });

  const unsubscribeAll = (): void => {
    unsubscribers.forEach((unsub) => unsub());
  };
  return [tooltipEls, unsubscribeAll];
};

export const overrideLegendTooltips = (legendContainerEl: HTMLElement): [HTMLElement[], () => void] => {
  const textEls = legendContainerEl.querySelectorAll('[data-tooltip]');
  const tooltipEls: HTMLElement[] = [];
  const unsubscribers: (() => void)[] = [];

  textEls?.forEach((textEl, index) => {
    const tooltipText = textEl.getAttribute('data-tooltip');
    const tooltipHtml = decodeURIComponent(tooltipText);
    const tooltipEl = buildTooltipEl(tooltipHtml);
    tooltipEl.classList.add('!p-0'); // Remove padding from the container since it's already included in the tooltip content
    document.body.appendChild(tooltipEl);
    tooltipEls[index] = tooltipEl;
    const triggerEl = textEl.parentElement;
    const tooltipInstance = new Tooltip(tooltipEl, triggerEl);

    const clickHandler = (): void => {
      tooltipInstance.show();
    };
    triggerEl.addEventListener('click', clickHandler);

    unsubscribers.push(() => {
      triggerEl.removeEventListener('click', clickHandler);
      tooltipEl?.remove();
      tooltipInstance.destroy();
    });
  });

  const unsubscribeAll = (): void => {
    unsubscribers.forEach((unsub) => unsub());
  };
  return [tooltipEls, unsubscribeAll];
};

export const manualShowDataPointTooltip = (event: MouseEvent, chartEl: HTMLElement, dataPointIndex: number): void => {
  // Search for the legend item that corresponds to the data point
  // and extract the tooltip text from it
  const legendContainerEl = chartEl.querySelector('.apexcharts-legend');
  const legendItemEl = legendContainerEl.querySelector(`.apexcharts-legend-text[rel="${dataPointIndex + 1}"]`);
  const legendTooltipContainerEl = legendItemEl.querySelector('[data-tooltip]');
  const legendTooltipText: string = legendTooltipContainerEl.getAttribute('data-tooltip');
  const legendTooltipHtml = decodeURIComponent(legendTooltipText);

  let tooltipEl: HTMLElement;

  const hideTooltip = (): void => {
    if (tooltipEl) {
      tooltipEl?.remove();
    }
  };
  const documentClickHandler = (e): void => {
    if (tooltipEl && !tooltipEl.contains(e.target as Node)) {
      hideTooltip();
      document.removeEventListener('click', documentClickHandler);
    }
  };
  const showTooltip = (): void => {
    hideTooltip();
    tooltipEl = buildTooltipEl(legendTooltipHtml, false);
    document.body.appendChild(tooltipEl);
    tooltipEl.classList.add('!p-0'); // Remove padding from the container since it's already included in the tooltip content
    tooltipEl.classList.add('absolute');

    // the tooltip is positioned above the cursor
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const top = event.pageY - tooltipRect.height;
    let left = event.pageX - tooltipRect.width / 2;
    if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width;
    }
    if (left < 0) {
      left = 0;
    }

    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${left}px`;

    // setTimeout to prevent tooltip from being removed immediately
    setTimeout(() => {
      document.body.addEventListener('click', documentClickHandler);
    }, 0);
  };
  showTooltip();
};

export const handleDonutChartDataPointSelection = (
  event: MouseEvent,
  chartContext: ChartContext,
  config: ChartConfig,
): void => {
  const selectedDataPoints = config.selectedDataPoints[0];
  if (!selectedDataPoints?.length) {
    return;
  }
  const chartEl: HTMLElement = chartContext.el;
  const dataPointIndex: number = config.dataPointIndex;
  manualShowDataPointTooltip(event, chartEl, dataPointIndex);
};

export const donutChartLegendFormater = (legendName: string, conf: ChartConfig): string => {
  const tooltipData = buildDonutChartTooltip(conf);
  return `<span data-tooltip="${encodeURIComponent(tooltipData)}">${legendName}</span>`;
};
